/**
 * Google Tag Manager Event Tracking Utility
 * Centralized GTM event tracking for BMW Configurator - v2
 */

// GTM Event Categories
export const GTM_CATEGORIES = {
    TRIM: 'Trim Selection',
    COLOR: 'Color Selection',
    VIEW: 'View Toggle',
    HOTSPOT: 'Hotspot Interaction',
    NAVIGATION: 'Navigation',
    POPUP: 'Popup Interaction',
    ANIMATION: 'Animation',
    MODEL: 'Model Interaction'
};

// GTM Event Actions
export const GTM_ACTIONS = {
    // Trim
    TRIM_SELECTED: 'Trim Selected',
    TRIM_CONFIRMED: 'Trim Confirmed',

    // Colors
    EXTERIOR_COLOR_SELECTED: 'Exterior Color Selected',
    INTERIOR_COLOR_SELECTED: 'Interior Color Selected',
    INTERACTION_BAR_COLOR_CHANGED: 'Interaction Bar Color Changed',

    // View Toggles
    TOGGLE_TO_INTERIOR: 'Toggle to Interior',
    TOGGLE_TO_EXTERIOR: 'Toggle to Exterior',

    // Hotspots - Exterior
    HEADLIGHT_CLICKED: 'Headlight Hotspot Clicked',
    TAILLIGHT_CLICKED: 'Taillight Hotspot Clicked',
    CLEAN_LINES_CLICKED: 'Clean Lines Hotspot Clicked',
    DOOR_HANDLE_TAPPED: 'Door Handle Tapped',
    WHEELS_CLICKED: 'Wheels Hotspot Clicked',
    SOPHISTICATED_TOUCH_CLICKED: 'Sophisticated Touch Clicked',
    DRAMATIC_FRAMING_CLICKED: 'Dramatic Framing Clicked',
    SCULPTED_STRENGTH_CLICKED: 'Sculpted Strength Clicked',
    POWER_PRECISION_CLICKED: 'Power Meets Precision Clicked',

    // Hotspots - Interior
    TRUNK_CARGO_CLICKED: 'Trunk Cargo Hotspot Clicked',
    COMFORT_CLICKED: 'Comfort Hotspot Clicked',
    REAR_SPACE_CLICKED: 'Rear Space Hotspot Clicked',
    CONTROL_FINGERTIPS_CLICKED: 'Control At Your Fingertips Clicked',
    TECH_FOCUSED_CLICKED: 'Tech Focused Clicked',
    MOTORSPORT_DNA_CLICKED: 'Motorsport DNA Clicked',

    // Navigation
    BACK_BUTTON_CLICKED: 'Back Button Clicked',
    HOTSPOT_BACK_CLICKED: 'Hotspot Back Clicked',

    // Popup
    POPUP_OPENED: 'Popup Opened',
    CONFIGURE_CLICKED: 'Configure Button Clicked',
    FIND_DEALER_CLICKED: 'Find Dealer Clicked',

    // Cargo
    CARGO_SELECTED: 'Cargo Item Selected',

    // Sliders
    SEAT_SLIDER_ADJUSTED: 'Seat Slider Adjusted',
    HEATER_TOGGLED: 'Heater Toggled',

    // 360
    THREE_SIXTY_CLICKED: '360 View Clicked',

    // Calipers
    CALIPER_TOGGLED: 'Caliper Toggled',

    // Backgrounds
    BACKGROUND_CHANGED: 'Background Changed',

    // Screens
    MOBILE_SCREEN_SHOWN: 'Mobile Screen Shown',
    TRIM_PAGE_VIEWED: 'Trim Page Viewed',

    // AR
    AR_BUTTON_CLICKED: 'AR Button Clicked'
};

/**
 * Send event to Google Tag Manager
 * @param {string} category - Event category
 * @param {string} action - Event action
 * @param {string} label - Event label (optional)
 * @param {number} value - Event value (optional)
 * @param {object} customData - Additional custom data (optional)
 */
export const sendGTMEvent = (category, action, label = '', value = null, customData = {}) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
        const eventData = {
            event: 'bmw_interaction',
            event_category: category,
            event_action: action,
            event_label: label,
            event_value: value,
            ...customData
        };

        // Remove null/undefined values
        Object.keys(eventData).forEach(key => {
            if (eventData[key] === null || eventData[key] === undefined) {
                delete eventData[key];
            }
        });

        window.dataLayer.push(eventData);
        console.log('📊 GTM Event:', eventData);
    }
};

/**
 * Track trim selection
 */
export const trackTrimSelection = (trimName, trimType) => {
    sendGTMEvent(
        GTM_CATEGORIES.TRIM,
        GTM_ACTIONS.TRIM_SELECTED,
        trimName,
        null,
        { trim_type: trimType }
    );
};

/**
 * Track trim confirmation (Enter button)
 */
export const trackTrimConfirmation = (trimName, trimType) => {
    sendGTMEvent(
        GTM_CATEGORIES.TRIM,
        GTM_ACTIONS.TRIM_CONFIRMED,
        trimName,
        null,
        { trim_type: trimType }
    );
};

/**
 * Track exterior color selection
 */
export const trackExteriorColorSelection = (colorName, colorCode) => {
    sendGTMEvent(
        GTM_CATEGORIES.COLOR,
        GTM_ACTIONS.EXTERIOR_COLOR_SELECTED,
        colorName,
        null,
        { color_code: colorCode }
    );
};

/**
 * Track interior color selection
 */
export const trackInteriorColorSelection = (colorName, colorCode) => {
    sendGTMEvent(
        GTM_CATEGORIES.COLOR,
        GTM_ACTIONS.INTERIOR_COLOR_SELECTED,
        colorName,
        null,
        { color_code: colorCode }
    );
};

/**
 * Track interaction bar color change
 */
export const trackInteractionBarColorChange = (colorName, colorCode) => {
    sendGTMEvent(
        GTM_CATEGORIES.COLOR,
        GTM_ACTIONS.INTERACTION_BAR_COLOR_CHANGED,
        colorName,
        null,
        { color_code: colorCode }
    );
};

/**
 * Track view toggle
 */
export const trackViewToggle = (toView) => {
    const action = toView === 'interior'
        ? GTM_ACTIONS.TOGGLE_TO_INTERIOR
        : GTM_ACTIONS.TOGGLE_TO_EXTERIOR;

    sendGTMEvent(
        GTM_CATEGORIES.VIEW,
        action,
        toView
    );
};

/**
 * Track hotspot interaction
 */
export const trackHotspotClick = (hotspotName, hotspotType = 'exterior') => {
    sendGTMEvent(
        GTM_CATEGORIES.HOTSPOT,
        `${hotspotName} Clicked`,
        hotspotName,
        null,
        { hotspot_type: hotspotType }
    );
};

/**
 * Track back button clicks
 */
export const trackBackButton = (fromLocation) => {
    sendGTMEvent(
        GTM_CATEGORIES.NAVIGATION,
        GTM_ACTIONS.BACK_BUTTON_CLICKED,
        fromLocation
    );
};

/**
 * Track popup interactions
 */
export const trackPopupOpen = (popupType = 'configure') => {
    sendGTMEvent(
        GTM_CATEGORIES.POPUP,
        GTM_ACTIONS.POPUP_OPENED,
        popupType
    );
};

export const trackConfigureClick = (trimName) => {
    sendGTMEvent(
        GTM_CATEGORIES.POPUP,
        GTM_ACTIONS.CONFIGURE_CLICKED,
        trimName
    );
};

export const trackFindDealerClick = () => {
    sendGTMEvent(
        GTM_CATEGORIES.POPUP,
        GTM_ACTIONS.FIND_DEALER_CLICKED,
        'Find Dealer Button'
    );
};

/**
 * Track cargo selection
 */
export const trackCargoSelection = (cargoType) => {
    sendGTMEvent(
        GTM_CATEGORIES.HOTSPOT,
        GTM_ACTIONS.CARGO_SELECTED,
        cargoType
    );
};

/**
 * Track seat slider adjustment
 */
export const trackSeatSlider = (sliderType, value) => {
    sendGTMEvent(
        GTM_CATEGORIES.HOTSPOT,
        GTM_ACTIONS.SEAT_SLIDER_ADJUSTED,
        sliderType,
        value
    );
};

/**
 * Track 360 view click
 */
export const track360Click = () => {
    sendGTMEvent(
        GTM_CATEGORIES.VIEW,
        GTM_ACTIONS.THREE_SIXTY_CLICKED,
        '360 View Icon'
    );
};

/**
 * Track door handle tap
 */
export const trackDoorHandleTap = () => {
    sendGTMEvent(
        GTM_CATEGORIES.HOTSPOT,
        GTM_ACTIONS.DOOR_HANDLE_TAPPED,
        'Clean Lines Door Handle'
    );
};

/**
 * Track caliper toggle
 */
export const trackCaliperToggle = (isEnabled) => {
    sendGTMEvent(
        GTM_CATEGORIES.HOTSPOT,
        GTM_ACTIONS.CALIPER_TOGGLED,
        isEnabled ? 'Sport (Blue)' : 'Standard (Red)'
    );
};

/**
 * Track background change
 */
export const trackBackgroundChange = (backgroundName) => {
    sendGTMEvent(
        GTM_CATEGORIES.VIEW,
        GTM_ACTIONS.BACKGROUND_CHANGED,
        backgroundName
    );
};

/**
 * Track mobile screen shown
 */
export const trackMobileScreenShown = () => {
    sendGTMEvent(GTM_CATEGORIES.VIEW, GTM_ACTIONS.MOBILE_SCREEN_SHOWN, 'Mobile Only Screen');
};

/**
 * Track heater toggle
 * @param {boolean} isHeaterActive - Whether heater view is active
 */
export const trackHeaterToggle = (isHeaterActive) => {
    sendGTMEvent(
        GTM_CATEGORIES.HOTSPOT,
        GTM_ACTIONS.HEATER_TOGGLED,
        isHeaterActive ? 'Heater View' : 'Exterior View'
    );
};

/**
 * Track trim page view
 */
export const trackTrimPageView = () => {
    sendGTMEvent(
        GTM_CATEGORIES.VIEW,
        GTM_ACTIONS.TRIM_PAGE_VIEWED,
        'Trim Selection Page'
    );
};

/**
 * Track AR button click
 */
export const trackARButtonClick = () => {
    sendGTMEvent(
        GTM_CATEGORIES.POPUP,
        GTM_ACTIONS.AR_BUTTON_CLICKED,
        'AR Experience Button'
    );
};

/**
 * Track hotspot back button click
 */
export const trackHotspotBackClick = (hotspotName) => {
    sendGTMEvent(
        GTM_CATEGORIES.NAVIGATION,
        GTM_ACTIONS.HOTSPOT_BACK_CLICKED,
        hotspotName || 'Unknown Hotspot'
    );
};

/**
 * Facebook Pixel Event Tracking
 * These events track user journey through the hybrid configurator
 */

// Session storage key to track if BYO Start event was already fired
const BYO_START_TRACKED_KEY = 'fbq_byo_start_tracked';

/**
 * Track PlayHybridBYOStart event - fires ONCE per session when scene loads
 * Uses sessionStorage to ensure it only fires once per browser session
 */
export const trackPlayHybridBYOStart = () => {
    if (typeof window !== 'undefined' && window.fbq) {
        // Check if already tracked in this session
        const alreadyTracked = sessionStorage.getItem(BYO_START_TRACKED_KEY);

        if (!alreadyTracked) {
            window.fbq('track', 'PlayHybridBYOStart');
            sessionStorage.setItem(BYO_START_TRACKED_KEY, 'true');
        } else {
            console.log('ℹ️ Facebook Pixel: PlayHybridBYOStart already tracked this session');
        }
    }
};

/**
 * Track PlayHybridBYOComplete event - fires when popup appears (after 60s)
 */
export const trackPlayHybridBYOComplete = () => {
    if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'PlayHybridBYOComplete');
    }
};

/**
 * Track PlayHybridExitToSite event - fires when user clicks Get a Quote button
 */
export const trackPlayHybridExitToSite = () => {
    if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'PlayHybridExitToSite');
    }
};
